/**
 * @pg/cart: every cart change in the theme goes through here.
 *
 * - Talks to the Ajax Cart API and asks it to re-render every section on the page that
 *   declares `data-pg-cart-section` (cart drawer, header cart icon, cart page) in the same
 *   request, so the UI updates without a second round trip.
 * - Announces each change with Shopify's standard `shopify:cart:lines-update` event
 *   (@shopify/events). Apps, analytics, the wholesale module and AI agents listen to that one
 *   event instead of theme-specific globals; `event.promise` resolves with the new cart.
 * - Points Shopify's standard `openCart` action at our drawer.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { config, toast, t } from '@pg/core';
import { adoptSectionStyles, clearSectionCache } from '@pg/section-renderer';

const routes = config.routes;

/** Section ids that should be re-rendered after any cart change. */
function cartSections() {
  return [...new Set([...document.querySelectorAll('[data-pg-cart-section]')].map((el) => el.dataset.pgCartSection))];
}

/** Swap fresh section HTML returned by the Cart API into the page. */
function renderSections(sections = {}) {
  for (const [id, html] of Object.entries(sections)) {
    if (!html) continue;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    adoptSectionStyles(doc);
    for (const target of document.querySelectorAll(`[data-pg-cart-section="${CSS.escape(id)}"]`)) {
      const next = doc.querySelector(`[data-pg-cart-section="${CSS.escape(id)}"]`);
      if (next) target.replaceChildren(...next.childNodes);
    }
  }
  for (const count of document.querySelectorAll('[data-pg-cart-count]')) {
    count.classList.remove('is-bumped');
    requestAnimationFrame(() => count.classList.add('is-bumped'));
  }
}

async function request(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    const error = new Error(data.description || data.message || t('cart_error'));
    error.name = 'CartError';
    throw error;
  }
  return data;
}

export async function getCart() {
  const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/**
 * Run a cart mutation and broadcast it as a standard event.
 * @param {'add'|'update'|'remove'} action
 * @param {any[]} lines - Standard-event lines: { merchandiseId, quantity } for add, { id, quantity } otherwise
 * @param {() => Promise<any>} mutate - Performs the request, resolves with the API response
 * @param {{ context?: 'product'|'cart'|'dialog'|'standard-action', source?: EventTarget, detail?: object }} options
 */
async function run(action, lines, mutate, { context = 'product', source = document, detail = {} } = {}) {
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise, detail }));

  try {
    const response = await mutate();
    // /cart/change.js answers with the whole cart; /cart/add.js only with the added items.
    const cart = response.item_count !== undefined ? response : await getCart();
    clearSectionCache();
    renderSections(response.sections);
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { ...detail, items: cart.items } });
    document.dispatchEvent(new CustomEvent('pg:cart:changed', { detail: { action, cart, response } }));
    return { cart, response };
  } catch (error) {
    source.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
    toast(error.message, { icon: 'alert' });
    getCart()
      .then((cart) => deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true } }))
      .catch(deferred.reject);
    throw error;
  }
}

/**
 * Add one or more variants.
 * @param {{ id: number|string, quantity?: number, properties?: object, selling_plan?: number }[]} items
 * @param {{ context?: any, source?: EventTarget, detail?: object }} [options]
 */
export function addItems(items, options = {}) {
  const lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity || 1 }));
  return run(
    'add',
    lines,
    () => request(routes.cartAdd, { items, sections: cartSections().join(','), sections_url: location.pathname }),
    options,
  );
}

/**
 * Set a line's quantity (0 removes it).
 * @param {string} key - Line item key
 * @param {number} quantity
 * @param {{ context?: any, source?: EventTarget, detail?: object }} [options]
 */
export function changeLine(key, quantity, options = {}) {
  return run(
    quantity === 0 ? 'remove' : 'update',
    [{ id: key, quantity }],
    () => request(routes.cartChange, { id: key, quantity, sections: cartSections().join(','), sections_url: location.pathname }),
    { context: 'cart', ...options },
  );
}

/** Open the cart drawer, or go to the cart page when the drawer isn't on this page. */
export function openCart() {
  const drawer = /** @type {any} */ (document.querySelector('pg-cart-drawer'));
  if (drawer?.open) drawer.open();
  else location.href = routes.cart;
}

// Standard actions (Shopify.actions) are how apps and agents ask the theme to show the cart.
function configureActions() {
  const actions = /** @type {any} */ (window).Shopify?.actions;
  if (!actions) return;
  actions.openCart?.configure({ handler: async () => openCart() });
  actions.updateCart?.configure({ eventTarget: () => document.querySelector('pg-cart-drawer') ?? document });
}

if (/** @type {any} */ (window).Shopify?.actions) configureActions();
else document.addEventListener('DOMContentLoaded', configureActions, { once: true });
